// Lista-oldal „Export" gombja — a legacy CRM export-gombjának utódja: egy másodlagos gomb, mögötte a három
// formátum (Excel · CSV · JSON) legördülőben. Helye a toolbar `actions` slotjában, az „+ Új …" gomb ELŐTT
// (a legacy elrendezését követve: a kimenő művelet a másodlagos, a létrehozás az elsődleges).
//
// JOG: NINCS külön export-jog. A jog-modell rögzített döntése (`apps/etl/src/steps/rbac.ts`), hogy a legacy
// `export_*` kulcsok NEM migrálódnak: „amit listázni tudsz, azt exportálni is". Ezért a gomb ott van, ahol a
// lista maga megjelenik — külön kapu nélkül. (A sor-készletet a lista-végpont adja, tehát a szerver-oldali
// jog/RLS ugyanúgy fal marad: aki nem látja, annak az exportba sem kerül.)
//
// ÁLLAPOTOK: a gyűjtés lapozással megy (több kérés) → a gomb `pending` alatt tiltott és „Exportálás…"-t mutat;
// üres lista → tiltott (nincs mit exportálni), tooltippel. Siker/hiba toast a hívó dolga (ő ismeri a modult).
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ExportFormat } from '@/lib/export';

/** A formátumok FIX sorrendje: a leggyakoribb (Excel) elöl — a legacy `['xlsx','csv','json']` sorrendje. */
const FORMATS: { format: ExportFormat; labelKey: string }[] = [
  { format: 'xlsx', labelKey: 'common.export.xlsx' },
  { format: 'csv', labelKey: 'common.export.csv' },
  { format: 'json', labelKey: 'common.export.json' },
];

export interface ListExportButtonProps {
  /** A választott formátummal indítja az exportot (a hívó gyűjti a sorokat + jelez toastot). */
  onExport: (format: ExportFormat) => void;
  /** Fut-e éppen export (gyűjtés/írás) — a gomb ilyenkor tiltott. */
  pending: boolean;
  /** Van-e egyáltalán exportálható sor (üres/hibás listán a gomb tiltott). */
  hasRows: boolean;
}

export function ListExportButton({
  onExport,
  pending,
  hasRows,
}: ListExportButtonProps): JSX.Element {
  const { t } = useTranslation();

  if (!hasRows || pending) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* A tooltip fókuszálható span-en: a `disabled` gomb nem tüzel pointer-eventet (mint a ListNewButton). */}
          <span tabIndex={0}>
            <Button variant="outline" disabled>
              <ArrowDownTrayIcon className="h-4 w-4" />
              {pending ? t('common.export.pending') : t('common.export.label')}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {pending ? t('common.export.pendingHint') : t('common.export.emptyHint')}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <ArrowDownTrayIcon className="h-4 w-4" />
          {t('common.export.label')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {FORMATS.map(({ format, labelKey }) => (
          <DropdownMenuItem key={format} onSelect={() => onExport(format)}>
            {t(labelKey)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
